import type { TemplateResult } from 'lit';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { activityCodes, activityCodesCommon, businessEntityTypeOptions, sectorOptions } from 'src/store/selectors';
import '../../library/components/d-activity-codes-container.js';
import '../../library/editors/index.js';
import '../../library/editors/elements/d-select-dropdown.js';
import '../../library/editors/components/d-edit-html';
import '../../library/elements/d-label.js';
import '../../library/elements/d-section.js';
import '../../library/components/d-field-section';
import type { DocForLinking } from 'src/models/internal-linking';
import type { FeatureStates } from '../../store/selectors/featureStates';
import type { OrganizationEditItem } from './organization-edit-item';
import type { SectionField } from '../../library/components/section-field';
import type { Sector } from 'src/store/types';
import { t } from 'src/i18n';

/**
 *
 */
@customElement('d-organization-edit')
export class DOrganizationEdit extends LitElement {
  static readonly styles = css`
    :host {
      display: block;
    }
  `;
  @property({ type: Object })
  token = {};
  @property({ type: Object })
  editItem!: OrganizationEditItem;
  @property({ type: Array })
  activityCodes = [];
  @property({ type: Object })
  featureStates!: FeatureStates;
  @property({ type: Number })
  contentStickyTop = 0;
  @property({ type: Array })
  docsForLinking: DocForLinking[] = [];

  renderEditSector(): TemplateResult<1> {
    return html`
      <d-select-dropdown
        theme-page
        class="fullWidth"
        field="organization_sector"
        .options=${sectorOptions}
        .value=${this.editItem.sector}
        @value-changed=${(e: CustomEvent<{ value: Sector }>) => {
          e.stopPropagation();
          console.log('e.detail.value', e.detail.value);
          this.editItem = { ...this.editItem, sector: e.detail.value, activityCodes: [] };
          this.fireEditItemChanged();
        }}
      ></d-select-dropdown>
    `;
  }

  renderEditActivityCodes(): TemplateResult<1> {
    return html`
      <div id="codeContent" class="fullWidth">
        <d-label field="organization_activityCodes"></d-label>
        <d-activity-codes-container
          theme-page
          .sector=${this.editItem.sector}
          .codes=${activityCodes(this.editItem.sector)}
          .commonActivityCodes=${this.editItem.sector === 'HEALTH' ? activityCodesCommon() : []}
          .activityCodes=${this.editItem.activityCodes}
          @activity-codes-changed=${(e: CustomEvent<{ codes: string[] }>) => {
            e.stopPropagation();
            this.editItem = { ...this.editItem, activityCodes: e.detail.codes };
            this.fireEditItemChanged();
          }}
        ></d-activity-codes-container>
      </div>
    `;
  }

  render(): TemplateResult<1> {
    return html`
      <d-field-section
        .entityType=${'organization'}
        .fields=${this.editFields()}
        .item=${this.editItem}
        theme-page
        @item-changed=${(e) => this.onFieldSectionChanged(e)}
        @field-action=${(e) => this.onFieldAction(e)}
      ></d-field-section>
      ${this.featureStates.organizationComplete
        ? html` <d-edit-html
            theme-page
            field="organization_notes"
            .value=${this.editItem.notes}
            .docsForLinking=${this.docsForLinking}
            .contentStickyTop=${this.contentStickyTop}
            @value-changed=${(e: CustomEvent<{ value: string }>) => {
              this.editItem = { ...this.editItem, notes: e.detail.value };
              this.fireEditItemChanged();
            }}
          ></d-edit-html>`
        : nothing}
    `;
  }

  protected onFieldSectionChanged(e) {
    this.editItem = e.detail.value;
    this.fireEditItemChanged();
  }

  protected onFieldAction(e) {
    this[e.detail.value]();
  }

  private editFields(): SectionField[] {
    const features = this.featureStates;
    return [
      {
        field: 'name',
        type: 'edit-text',
        class: 'minWidth300',
      },
      {
        field: 'businessEntityType',
        type: 'edit-dropdown',
        options: businessEntityTypeOptions,
        placeholder: t('organization.selectPlaceholder'),
        class: 'minWidth300',
      },
      {
        type: 'custom',
        render: () => this.renderEditSector(),
      },
      {
        type: 'custom',
        render: () => this.renderEditActivityCodes(),
        condition: activityCodes(this.editItem.sector).length > 0,
      },
      {
        field: 'organizationNumber',
        type: 'edit-text',
        class: 'minWidth300',
        condition: features.organizationComplete,
      },
      {
        field: 'herNumber',
        type: 'edit-text',
        class: 'minWidth300',
        condition: features.organizationComplete && this.editItem.sector === 'HEALTH',
      },
      {
        field: 'phone',
        type: 'edit-text',
        class: 'minWidth200',
        condition: features.organizationComplete,
      },
      {
        field: 'url',
        type: 'edit-text',
        class: 'minWidth200',
        condition: features.organizationComplete,
      },
      {
        field: 'email',
        type: 'edit-text',
        class: 'minWidth200',
        condition: features.organizationComplete,
      },
      {
        type: 'break',
      },
      {
        field: 'officeAddress',
        type: 'edit-textarea',
        class: 'minWidth300',
        condition: features.organizationComplete,
      },
      {
        field: 'mailAddress',
        type: 'edit-textarea',
        class: 'minWidth300',
        condition: features.organizationComplete,
      },
      {
        type: 'break',
      },
    ];
  }

  private fireEditItemChanged() {
    this.dispatchEvent(
      new CustomEvent<OrganizationEditItem>('edit-item-changed', {
        composed: true,
        bubbles: true,
        detail: this.editItem,
      }),
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-organization-edit': DOrganizationEdit;
  }
}
